import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import React from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { Button } from '../catalyst-ui-kit/button';
import { Loader } from '../Loader';
import { AlertAction, AlertVariant } from './Alert.types';
import { AlertActions } from './AlertActions';

const variantStyles = {
  plain: {
    background: 'bg-backgroundPrimary dark:bg-backgroundPrimary',
    foreground: 'text-contentBlue dark:text-contentBlue fill-contentBlue dark:fill-contentBlue',
    body: 'text-contentSecondary dark:text-contentSecondary fill-contentSecondary dark:fill-contentSecondary',
    button: 'blue',
  },
  info: {
    background: 'bg-blue-950 dark:bg-blue-950',
    foreground: 'text-contentBlue dark:text-contentBlue fill-contentBlue dark:fill-contentBlue',
    body: 'text-contentSecondary dark:text-contentSecondary',
    button: 'blue',
  },
  success: {
    background: 'bg-green-950 dark:bg-green-950',
    foreground: 'text-contentGreen dark:text-contentGreen fill-contentGreen dark:fill-contentGreen',
    body: 'text-contentSecondary dark:text-contentSecondary',
    button: 'green',
  },
  error: {
    background: 'bg-red-950 dark:bg-red-950',
    foreground: 'text-contentRed dark:text-contentRed fill-contentRed dark:fill-contentRed',
    body: 'text-contentSecondary dark:text-contentSecondary',
    button: 'red',
  },
  warning: {
    background: 'bg-amber-950 dark:bg-amber-950',
    foreground: 'text-contentAmber dark:text-contentAmber fill-contentAmber dark:fill-contentAmber',
    body: 'text-contentSecondary dark:text-contentSecondary',
    button: 'orange',
  },
} as const;

export interface AlertProps {
  variant?: AlertVariant;
  title?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }> | React.ReactNode;
  loading?: boolean;
  className?: string;
  actions?: AlertAction[];
  children?: React.ReactNode;
  onClose?: () => void;
}

const AlertIcon = ({
  variant = 'plain',
  loading,
  icon,
}: Pick<AlertProps, 'loading' | 'icon' | 'variant'>) => {
  const iconClass = twJoin('size-full', variantStyles[variant].foreground);

  if (icon) {
    if (
      typeof icon === 'function' ||
      typeof (icon as unknown as React.Component).render === 'function'
    ) {
      const IconComponent = icon as React.ComponentType<{ className?: string }>;
      return <IconComponent className={iconClass} aria-hidden="true" />;
    }
    return icon;
  }

  if (loading) {
    return (
      <div className={twJoin('rounded-full p-px', iconClass)}>
        <Loader className="size-full" />
      </div>
    );
  }

  switch (variant) {
    case 'error':
      return <ExclamationCircleIcon className={iconClass} />;
    case 'warning':
      return <ExclamationTriangleIcon className={iconClass} />;
    case 'success':
      return <CheckCircleIcon className={iconClass} />;
    default:
      return <InformationCircleIcon className={iconClass} />;
  }
};

export const Alert: React.FC<AlertProps> = ({
  variant = 'plain',
  title,
  icon,
  loading,
  className,
  actions,
  children,
  onClose,
}) => {
  // Temporary backwards compatible props to avoid merge conflicts
  if (!children && title) {
    children = title;
    title = undefined;
  }

  const compact = !title;

  return (
    <div
      className={twMerge(
        'flex flex-col items-start gap-[10px] rounded border border-borderHoverOverlay',
        variantStyles[variant].background,
        compact ? 'p-2' : 'p-[14px]',
        className
      )}
      role="alert"
    >
      <div className="w-full gap-1">
        <div className={twJoin('flex w-full items-center gap-2', !compact ? 'p-1' : '')}>
          <div className={compact ? 'size-4' : 'size-6'}>
            <AlertIcon variant={variant} loading={loading} icon={icon} />
          </div>
          <div
            className={twJoin(
              'flex-1 grow',
              compact ? 'text-xs font-medium' : 'text-base font-normal'
            )}
          >
            {compact ? children : title}
          </div>
          {compact && actions && actions.length > 0 && (
            <AlertActions
              actions={actions}
              variantStyles={variantStyles[variant]}
              className="flex items-center gap-1"
            />
          )}
          {onClose && (
            <Button plain onClick={() => onClose()} className="-m-1 p-1 sm:p-1">
              <XMarkIcon
                className={twJoin('!m-0 size-4 fill-contentPrimary', variantStyles[variant].body)}
              />
            </Button>
          )}
        </div>
        {!compact ? (
          <p className={twJoin('text-sm font-normal', variantStyles[variant].body)}>{children}</p>
        ) : null}
      </div>

      {!compact && actions && actions.length > 0 && (
        <div className="flex gap-2">
          <AlertActions
            actions={actions}
            variantStyles={variantStyles[variant]}
            className="flex items-center gap-1"
          />
        </div>
      )}
    </div>
  );
};
